import { Select } from '@pyxis/react';
import * as RadixSelect from '@radix-ui/react-select';
import cx from 'classnames';
import { useContext } from 'react';
import React, { useState, useCallback } from 'react';

import { Jobs } from '../../components/Jobs/Jobs';
import { Button } from '../../components/buttons/Button';
import { IconLoader } from '../../components/images/IconLoader';
import { ScriptJsonLd } from '../../components/meta/ScriptJsonLd';
import { Background } from '../../components/module/Background';
import { Bleed } from '../../components/module/Bleed';
import { Spacing } from '../../components/module/Spacing';
import { Width } from '../../components/module/Width';
import { Wrapper } from '../../components/module/Wrapper';
import { PageContext } from '../../context/PageContext';
import { getURLForPath } from '../../helpers/sitemap/getURLForPath';
import {
  OtherCountryCodeType,
  OTHER_COUNTRY_CODE,
  WorkableDepartmentType,
  WorkableListItemType,
  WorkableLocationType,
} from '../../types';

export type JobFinderProps = {
  jobs?: WorkableListItemType[];
  countries?: {
    name?: string;
    code?: WorkableLocationType['code'] | OtherCountryCodeType;
  }[];
  departments?: {
    name?: string;
    code?: WorkableDepartmentType['name'] | string;
  }[];
};

type OfficeType = {
  city?: string;
  country?: string;
};

const titleClassName =
  'text-title-3xl-sm md:text-title-3xl-md lg:text-title-3xl-lg font-bold decoration-2 underline-offset-4 focus:outline-none select-none';

const JOBS_PER_PAGE = 10;

export const JobFinder = ({ jobs, countries, departments }: JobFinderProps) => {
  const { config, locale, sitemap } = useContext(PageContext);
  const translations = config.translations;

  const [filterCountry, setFilterCountry] = useState<string | null>(null);
  const [filterOffice, setFilterOffice] = useState<string>('');
  const [filterDepartment, setFilterDepartment] = useState<string>('');
  const [filterRemote, setFilterRemote] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(1);

  const filteredJobs = jobs.filter((job) => {
    const isRemote = job.location?.telecommuting;
    const countryCode = job.location?.country_code;
    const city = job.location?.city;
    const department = job.department;

    // if (isRemote === true && filterRemote === false) return false;
    if (isRemote === false && filterRemote === true) return false;

    // country
    if (
      filterCountry &&
      filterCountry !== OTHER_COUNTRY_CODE &&
      countryCode !== filterCountry
    )
      return false;

    // international, other countries not in list
    if (
      filterCountry &&
      filterCountry === OTHER_COUNTRY_CODE &&
      countries.find(({ code }) => code === countryCode)
    )
      return false;

    // office
    if (filterOffice && city !== filterOffice) return false;

    // department
    if (filterDepartment && department !== filterDepartment) return false;

    return true;
  });

  const hasNext = filteredJobs.length > currentPage * JOBS_PER_PAGE;

  const officesDict = jobs
    ?.filter(({ location }) => Boolean(location?.city))
    ?.reduce((prev, curr) => {
      if (!curr.location?.city) return prev;
      if (curr.location?.country_code !== filterCountry) return prev;
      return {
        ...prev,
        [curr.location?.city]: {
          city: curr.location?.city,
          country: curr.location?.country,
        },
      };
    }, {} as Record<string, OfficeType>);
  const offices = Object.values(officesDict);

  const jobOverviewPagePath: string = sitemap.find(({ _id }) => _id === 'page_jobs')
    ?.paths?.[locale];

  const jobsJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: jobs
      ?.map((job, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: job.title,
        url: getURLForPath(
          config?.general?.domain,
          `${jobOverviewPagePath}/${job.slug}`,
          locale,
        ),
      }))
      .filter(Boolean),
  };

  const clearFilters = useCallback(() => {
    setFilterCountry(null);
    setFilterOffice('');
    setFilterDepartment('');
    setFilterRemote(false);
    setCurrentPage(1);
  }, []);

  return (
    <div className="-mt-12 sm:-mt-16 md:tablet:-mt-12 tablet:-mt-16 lg:-mt-12 xl:-mt-20">
      <Bleed bleed="sm" className="relative z-20 pointer-events-none">
        <Spacing space={{ top: 'none', bottom: 'none' }}>
          <Width width="outer" className="pt-32">
            <Background
              theme={{ background: 'neutral-95', rounded: { top: 'lg' } }}
              className="border border-transparent"
            >
              <Width width="inner">
                <div className="flex flex-col gap-4 md:gap-8 border-2 border-neutral-85 bg-white p-6 md:p-10 xl:p-14 -translate-y-1/2 rounded-2xl -mb-24 shadow-lg max-w-[900px] mx-10 2xl:mx-0 pointer-events-auto">
                  {/* title with select */}
                  <div className="grid lg:grid-cols-12 flex-wrap gap-4 leading-none relative">
                    <div className="lg:col-span-9">
                      <div>
                        <span className="text-title-3xl-sm md:text-title-3xl-md lg:text-title-3xl-lg font-bold text-neutral-base whitespace-nowrap">
                          {translations?.positions_in?.[locale]}
                        </span>
                        &nbsp;&nbsp;&nbsp;
                        <RadixSelect.Root
                          value={filterCountry}
                          onValueChange={setFilterCountry}
                        >
                          <RadixSelect.Trigger
                            className={cx(
                              'underline text-brand-base hover:text-brand-dark focus:text-brand-dark',
                              titleClassName,
                            )}
                            asChild
                          >
                            <button>
                              <RadixSelect.Value />
                              <RadixSelect.Icon>
                                <IconLoader
                                  icon="chevron"
                                  className="inline-block w-6 h-6 md:w-9 md:h-9 lg:w-11 lg:h-11 text-brand-base align-middle"
                                />
                              </RadixSelect.Icon>
                            </button>
                          </RadixSelect.Trigger>
                          <RadixSelect.Portal className="z-60">
                            <RadixSelect.Content className="ml-auto">
                              <RadixSelect.ScrollUpButton />
                              <RadixSelect.Viewport className="relative bg-white text-lg rounded-md shadow-lg">
                                <span className="block absolute inset-0 border-2 border-neutral-85 rounded-md pointer-events-none" />

                                {/* clear */}
                                <RadixSelect.Item
                                  value={null}
                                  className={cx(
                                    'p-4 hover:underline focus:underline focus:bg-neutral-95 hover:bg-neutral-95',
                                    titleClassName,
                                    {
                                      ['text-brand-base']: !filterCountry,
                                      ['text-neutral-base']: filterCountry,
                                    },
                                  )}
                                >
                                  <RadixSelect.ItemText>
                                    {translations?.all_countries?.[locale]}
                                  </RadixSelect.ItemText>
                                </RadixSelect.Item>

                                {countries?.map(({ name, code }) => (
                                  <RadixSelect.Item
                                    value={code}
                                    key={code}
                                    className={cx(
                                      'py-4 pl-4 pr-10 hover:underline focus:underline focus:bg-neutral-95 hover:bg-neutral-95',
                                      titleClassName,
                                      {
                                        ['text-brand-base']: code === filterCountry,
                                        ['text-neutral-base']:
                                          code !== filterCountry,
                                      },
                                    )}
                                  >
                                    <RadixSelect.ItemText>
                                      {name}
                                    </RadixSelect.ItemText>
                                  </RadixSelect.Item>
                                ))}
                              </RadixSelect.Viewport>
                              <RadixSelect.ScrollDownButton />
                            </RadixSelect.Content>
                          </RadixSelect.Portal>
                        </RadixSelect.Root>
                      </div>
                    </div>

                    {/* remote switch */}
                    <span className="flex items-start lg:justify-end gap-4 lg:col-span-3 lg:pt-7">
                      <span className="text-neutral-base text-lg mt-1">
                        {translations?.full_remote?.[locale]}
                      </span>
                      <label className="inline-flex relative items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filterRemote}
                          className="sr-only peer"
                          onChange={(e) =>
                            setFilterRemote((e.target as HTMLInputElement).checked)
                          }
                        />
                        <div className="w-11 h-6 bg-neutral-75 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-action-light rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-action-base"></div>
                      </label>
                    </span>
                  </div>

                  {Boolean(offices?.length) && Boolean(offices?.length > 1) && (
                    <div>
                      <Select
                        onChange={(e) =>
                          setFilterOffice((e.target as HTMLSelectElement).value)
                        }
                        placeholder={
                          filterOffice
                            ? translations?.all_office_locations?.[locale]
                            : translations?.office_location?.[locale]
                        }
                        label={
                          filterOffice
                            ? translations?.all_office_locations?.[locale]
                            : translations?.office_location?.[locale]
                        }
                        items={[
                          {
                            value: null,
                            label: filterOffice
                              ? translations?.all_office_locations?.[locale]
                              : translations?.office_location?.[locale] || '…',
                          },
                          ...offices.map(({ city }) => ({
                            value: city,
                            label: city,
                          })),
                        ]}
                        name="office"
                        value={filterOffice}
                        size="m"
                        className="[&_input]:rounded-[0.5em]"
                        isHiddenLabel
                      />
                    </div>
                  )}

                  {Boolean(departments?.length) &&
                    Boolean(departments?.length > 1) && (
                      <div>
                        <Select
                          onChange={(e) =>
                            setFilterDepartment(
                              (e.target as HTMLSelectElement).value,
                            )
                          }
                          placeholder={
                            filterDepartment
                              ? translations?.all_departments?.[locale]
                              : translations?.department?.[locale]
                          }
                          label={
                            filterDepartment
                              ? translations?.all_departments?.[locale]
                              : translations?.department?.[locale]
                          }
                          items={[
                            {
                              value: null,
                              label: filterDepartment
                                ? translations?.all_departments?.[locale]
                                : translations?.department?.[locale] || '…',
                            },
                            ...departments.map(({ name, code }) => ({
                              value: name,
                              label: name,
                            })),
                          ]}
                          name="department"
                          value={filterDepartment}
                          size="m"
                          className="relative [&_.form-dropdown-wrapper]:z-60"
                          isHiddenLabel
                        />
                      </div>
                    )}
                </div>
              </Width>
            </Background>
          </Width>
        </Spacing>
      </Bleed>

      <Wrapper
        theme={{
          space: { top: 'none', bottom: 'xl' },
          background: 'neutral-95',
          rounded: { top: 'none' },
        }}
        className="relative z-10"
      >
        <div className="pb-20">
          {Boolean(filteredJobs?.length) ? (
            <Jobs items={filteredJobs.slice(0, currentPage * JOBS_PER_PAGE)} />
          ) : (
            <div className="xs:mt-4 md:mt-0 mx-2 relative rounded-xl p-6 0 md:p-8 shadow-lg md:shadow-none bg-white md:bg-transparent mb-11">
              <p className="font-bold text-neutral-base text-title-sm-sm md:text-title-md-md lg:text-title-md-lg mb-6">
                {translations?.no_jobs_found?.[locale]}
              </p>
              <p>
                <Button
                  label={translations?.job_finder_clear_filters?.[locale]}
                  onClick={clearFilters}
                />
              </p>
            </div>
          )}
        </div>

        {hasNext && (
          <div className="text-center pb-20">
            <Button
              label={translations?.job_finder_show_more?.[locale]}
              onClick={() => setCurrentPage((n) => n + 1)}
            />
          </div>
        )}

        <ScriptJsonLd data={jobsJsonLd} />
      </Wrapper>
    </div>
  );
};

export default React.memo(JobFinder);
